"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ServicesOverview() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: actionsRef,
    isVisible: areActionsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="251903" id="tipologie-di-visto" className="bg-background text-foreground overflow-hidden py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-mono uppercase tracking-widest">
            <TrendingUp className="mr-1 size-3" />
            Standard Internazionali
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-primary">
            Tipologie di <span className="text-secondary">Visto</span>
          </h2>
          <p className="text-muted-foreground mt-4 text-lg md:text-xl font-default">
            Eccellenza nella gestione della mobilità globale. Offriamo soluzioni legali e amministrative per ogni esigenza di immigrazione professionale e privata.
          </p>
        </div>

        <div ref={actionsRef} className={`mt-16 flex flex-col items-center justify-center gap-4 sm:flex-row transition-all duration-1000 delay-200 ease-out ${areActionsVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <Button data-index="0" size="lg" className="w-full sm:w-auto min-w-[240px] bg-[#FFC829] text-primary hover:bg-[#FFC829]/90 rounded-sm font-default text-lg h-14 shadow-lg" asChild>
            <Link to="/TouristVisasConsularVisasPage">
              VISTI
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
          
          <Button size="lg" className="w-full sm:w-auto min-w-[240px] bg-[#FFC829] text-primary hover:bg-[#FFC829]/90 rounded-sm font-default text-lg h-14 shadow-lg" asChild>
            <Link to="https://decent-goblin.10web.cloud/servizi-per-l-immigrazione-e-regolarizzazione/">
              IMMIGRAZIONE
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}